import { Article } from '../types';

export const freePrepaidMeterMafDisrep2026: Article = {
    id: 'free-prepaid-meter-maf-disrep-2026-guide',
    title: 'How to Get a Free Prepaid Meter in Nigeria (2026 MAF & DISREP Guide)',
    slug: 'how-to-get-free-prepaid-meter-nigeria-maf-disrep',
    excerpt: 'Complete guide to getting a free prepaid electricity meter under the Meter Acquisition Fund (MAF) and DISREP initiatives in 2026.',
    category: 'Power Generation',
    date: 'May 30, 2026',
    readTime: '5 min read',
    author: 'power.ng Research',
    trending: true,
    keywords: ['free prepaid meter Nigeria 2026', 'Meter Acquisition Fund apply', 'DISREP free meter registration', 'get free meter Lagos Abuja'],
    metaDescription: 'How to get a free prepaid electricity meter in Nigeria under the 2026 MAF & DISREP programs. Requirements, registration portals, and how to avoid scams.',
    faqs: [
        {
            question: 'Is the prepaid meter truly free in Nigeria in 2026?',
            answer: 'Yes, prepaid meters distributed under the government-backed Meter Acquisition Fund (MAF) and the Distribution Sector Recovery Programme (DISREP) are 100% free at the point of installation. No payments should be made to installers or DisCo staff.'
        },
        {
            question: 'How do I apply for the free prepaid meter program?',
            answer: 'You must register and update your KYC (Know Your Customer) details on your local DisCo\'s official portal. The DisCo manages the rollout schedule feeder-by-feeder.'
        },
        {
            question: 'What is the difference between the free MAF scheme and the MAP scheme?',
            answer: 'Under MAF/DISREP, you do not pay anything, but you must wait for your DisCo\'s deployment schedule. Under the MAP (Meter Asset Provider) scheme, you pay for the meter upfront, and the cost is refunded to you via monthly energy tokens.'
        },
        {
            question: 'How long does it take to get the free meter installed?',
            answer: 'Rollout is scheduled based on feeder priority (prioritizing Band A and Band B). Once your feeder is scheduled and your KYC is verified, installation usually takes 5-15 working days.'
        }
    ],
    content: `## Quick Answer: How to Access the Free Meter Program
To get a free prepaid meter in Nigeria in 2026, you must complete the KYC registration on your specific Distribution Company's (DisCo) website. These free meters are funded under the Federal Government's **Meter Acquisition Fund (MAF) Tranche B** and the **Distribution Sector Recovery Programme (DISREP)**. The rollout is executed feeder-by-feeder, prioritizing unmetered Band A and Band B customers.

---

## 1. What are the 2026 Free Meter Programs (MAF & DISREP)?

To close the metering gap of over 5.1 million customers still subjected to estimated billing, the Federal Government and NERC launched two key free metering initiatives in 2026:

*   **Meter Acquisition Fund (MAF):** Funded via tariff revenues and external grants, NERC manages this fund to acquire and distribute smart prepaid meters to customers at no cost.
*   **Distribution Sector Recovery Programme (DISREP):** A World Bank-backed project aimed at improving grid infrastructure and providing free smart meters to reduce technical and commercial losses.

Both programs provide meters **100% free of charge** to eligible customers.

---

## 2. Free Meter Scheme (MAF) vs. Paid Meter Scheme (MAP)
It is important not to confuse the free programs with the **Meter Asset Provider (MAP)** scheme:

Already paid for a MAP meter? Use our [prepaid meter refund guide](/articles/prepaid-meter-map-refund-nigeria) to find official refund-token portals, understand the repayment schedule and investigate missing credits.

| Feature | Free Schemes (MAF / DISREP) | Paid Scheme (MAP) |
| :--- | :--- | :--- |
| **Upfront Cost** | **₦0.00** | Single Phase: ~₦130k-145k <br> Three Phase: ~₦220k-250k |
| **Speed** | Slow (depends on DisCo schedule) | Fast (within 10 days of payment) |
| **Refund** | Not applicable (already free) | Refunded via energy credits over time |
| **Eligibility** | Selected feeders (mostly Band A/B first) | Open to all customers |

---

## 3. Step-by-Step Guide to Getting Your Free Meter

### Step 1: Confirm Your Feeder Band Status
Because free meters are deployed in phases, DisCos prioritize customers on **Band A and Band B feeders** (those receiving 16-20+ hours of power daily) to ensure billing accuracy. Check your recent bill or use the **power.ng Tariff Band Checker** to confirm your current band.

### Step 2: Complete the KYC Update on Your DisCo's Portal
Each DisCo coordinates the free meter registration through their official portal:
*   **Ikeja Electric (IKEDC):** Visit [map.ikejaelectric.com](https://map.ikejaelectric.com) or their dedicated KYC page.
*   **Eko DisCo (EKEDP):** Visit [ekedp.com/meter](https://ekedp.com/meter).
*   **Abuja DisCo (AEDC):** Visit [abujaelectricity.com/map](https://abujaelectricity.com/map).
*   **Kano DisCo (KEDCO):** Visit [kedco.ng](https://www.kedco.ng).

Login or register with your **Account Number**, update your contact information, upload a valid government ID (NIN, voter card, etc.), and submit.

### Step 3: Site Inspection
The DisCo's technical team will visit your premises for a pre-installation survey. They verify:
*   The safety of your internal wiring.
*   Separation of service lines if it is a multi-tenant property.
*   The type of meter required (Single Phase vs. Three Phase).

### Step 4: Feeder Rollout Schedule & Installation
Once your KYC is approved and the survey is complete, the DisCo will notify you when your feeder is scheduled for installation. Installers will visit your location to fit the meter.

---

## 4. Crucial Warnings & Anti-Fraud Advice

Because of high demand, fraudsters often exploit unmetered customers by claiming they can "fast-track" free meters.

> [!CAUTION]
> **Zero Cash Policy:** Do not pay cash to any installer, technician, or DisCo field staff. Under the MAF and DISREP guidelines, the meter and installation services are completely free. If anyone demands a payment to install a "free" meter, refuse and report it immediately.

### What to Do If You Encounter Extortion
If an agent demands money or if you experience excessive delays after your feeder has been cleared for installation:
1.  **Get Details:** Note the agent's name, ID number, and DisCo branch.
2.  **Report to DisCo:** Contact your DisCo's whistleblowing or customer service line.
3.  **Escalate to NERC:** Send an email to **complaints@nerc.gov.ng** or call the NERC customer care lines. The Electricity Act 2023 protects you from billing harassment and grants you the right to a functional meter.`
};
